import { computed, getCurrentInstance, ref } from "vue";
import { getChatterFeed } from "@/api";
import { LD_FLAG_IDEA_MDP_583 } from "@components/constants/launchDarklyFlagKeys";
import { feedItemKey } from "./commentUtils";

const currentAccountId = () => JSON.parse(localStorage.getItem("currentAccountID"));

/** Appends `incoming` to `existing`, skipping ids that are already present. */
export const mergeFeedItems = (existing, incoming) => {
  const seen = new Set(existing.map(feedItemKey));
  const fresh = [];
  for (const item of incoming) {
    const key = feedItemKey(item);
    if (seen.has(key)) continue;
    seen.add(key);
    fresh.push(item);
  }
  return [...existing, ...fresh];
};

/**
 * State and fetching for the combined Chatter drawer (idea-MDP-583).
 * The opener calls `refresh()` when the drawer opens and after `posted`, and
 * `loadOlder()` for "Load older activity".
 * @param {{ objectType: string|(() => string), getObjectId: () => (string|number|null) }} options
 */
export function useChatterFeed({ objectType, getObjectId }) {
  const proxy = getCurrentInstance()?.proxy;
  const enabled = ref(false);
  const items = ref([]);
  const nextCursor = ref(null);
  const unseenMentionCount = ref(0);
  const loading = ref(false);
  const hasMore = computed(() => Boolean(nextCursor.value));
  let filter = "all";
  let requestSeq = 0;

  const resolveFlag = async () => {
    try {
      if (typeof proxy?.$getFlag === "function") {
        enabled.value = Boolean(await proxy.$getFlag(LD_FLAG_IDEA_MDP_583, false));
      }
    } catch (error) {
      console.error("Failed to resolve idea-MDP-583", error);
      enabled.value = false;
    }
    return enabled.value;
  };

  const fetchPage = async (cursor) => {
    const resolvedType = typeof objectType === "function" ? objectType() : objectType;
    const objectId = getObjectId();
    if (!resolvedType || objectId == null || objectId === "") return;
    const seq = ++requestSeq;
    loading.value = true;
    try {
      const { data } = await getChatterFeed(
        currentAccountId(),
        resolvedType,
        objectId,
        filter,
        cursor
      );
      if (seq !== requestSeq) return;
      const results = data?.results || [];
      items.value = cursor ? mergeFeedItems(items.value, results) : mergeFeedItems([], results);
      nextCursor.value = data?.next_cursor || null;
      if (typeof data?.unseen_mention_count === "number") {
        unseenMentionCount.value = data.unseen_mention_count;
      }
    } catch (error) {
      if (seq !== requestSeq) return;
      console.error("Failed to fetch chatter feed", error);
      proxy?.$notifications?.error("Failed to fetch chatter data.");
    } finally {
      if (seq === requestSeq) loading.value = false;
    }
  };

  const refresh = () => fetchPage(null);

  const loadOlder = async () => {
    if (!nextCursor.value || loading.value) return;
    await fetchPage(nextCursor.value);
  };

  const setFilter = (value) => {
    filter = value;
    return refresh();
  };

  const reset = () => {
    requestSeq++;
    items.value = [];
    nextCursor.value = null;
    unseenMentionCount.value = 0;
    loading.value = false;
  };

  return {
    enabled,
    items,
    hasMore,
    unseenMentionCount,
    loading,
    resolveFlag,
    refresh,
    loadOlder,
    setFilter,
    reset,
  };
}
